import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Share2, HardDrive, Play, Trash2, Music2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatCount, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { InstitutionMark, VerifiedBadge, Avatar } from "@/components/Brand";
import { MediaOverlays } from "@/components/shorts/Overlays";
import { filterCss } from "@/lib/filters";
import { BlurBackdrop } from "@/components/shorts/BlurBackdrop";

function Action({ icon: Icon, label, onClick, active, testid }) {
  return (
    <button onClick={onClick} className="flex flex-col items-center gap-1 text-xs font-bold" data-testid={testid}>
      <span className={`grid h-12 w-12 place-items-center rounded-full bg-black/40 backdrop-blur transition-transform active:scale-90 ${active ? "text-rose-500" : "text-white"}`}>
        <Icon size={24} fill={active ? "currentColor" : "none"} />
      </span>
      {label}
    </button>
  );
}

function DeleteDialog({ open, onOpenChange, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="delete-short-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir publicação?</AlertDialogTitle>
          <AlertDialogDescription>A publicação e seus comentários serão apagados permanentemente da plataforma.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-short-cancel">Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-500" data-testid="delete-short-confirm">Excluir</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function ShortCard({ s, muted, focused, onComments, onUpdate, onDeleted }) {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const viewTimer = useRef(null);
  const [paused, setPaused] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const { user } = useAuth();
  const canDelete = user && (user.role === "admin" || user.id === s.author_id || user.id === s.destination_id);
  const remove = async () => {
    try {
      await api.delete(`/shorts/${s.id}`);
      toast.success("Publicação excluída");
      onDeleted(s.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    const v = videoRef.current;
    const a = audioRef.current;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        v?.play().then(() => setPaused(false)).catch(() => setPaused(true));
        a?.play().catch(() => {});
        clearTimeout(viewTimer.current);
        viewTimer.current = setTimeout(() => { api.post(`/shorts/${s.id}/view`).catch(() => {}); }, 1000);
      } else {
        clearTimeout(viewTimer.current);
        v?.pause();
        if (a) { a.pause(); a.currentTime = 0; }
      }
    }, { threshold: 0.6 });
    io.observe(ref.current);
    if (focused) ref.current.scrollIntoView({ block: "start" });
    return () => { io.disconnect(); clearTimeout(viewTimer.current); };
  }, [focused, s.id]);

  const toggle = () => {
    const v = videoRef.current;
    const a = audioRef.current;
    const isPaused = v ? v.paused : a?.paused;
    if (isPaused === undefined) return;
    if (isPaused) { v?.play(); a?.play().catch(() => {}); setPaused(false); } else { v?.pause(); a?.pause(); setPaused(true); }
  };
  const like = async () => {
    const { data } = await api.post(`/shorts/${s.id}/like`);
    onUpdate({ ...s, likes: data.likes, liked_by_me: data.liked });
  };
  const share = async () => {
    const url = `${window.location.origin}/curtas/${s.id}`;
    try {
      if (navigator.share) await navigator.share({ title: s.caption, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copiado!"); }
    } catch { return; }
    const { data } = await api.post(`/shorts/${s.id}/share`);
    onUpdate({ ...s, shares: data.shares });
  };

  return (
    <article ref={ref} className="relative h-[100dvh] w-full snap-start snap-always overflow-hidden bg-black" data-testid={`short-card-${s.id}`} data-focused={focused ? "true" : undefined}>
      <BlurBackdrop src={s.poster_url} />
      {s.media_type === "photo" ? (
        <img src={s.poster_url} alt={s.caption} onClick={toggle} style={{ filter: filterCss(s.edits?.filter) }} className="absolute inset-0 h-full w-full object-contain" data-testid="short-photo" />
      ) : (
        <video ref={videoRef} src={s.video_url} poster={s.poster_url} muted={muted} loop playsInline preload="metadata" onClick={toggle} style={{ filter: filterCss(s.edits?.filter) }} className="absolute inset-0 h-full w-full object-contain" data-testid="short-video" />
      )}
      {s.edits && <MediaOverlays edits={s.edits} />}
      {s.music && <audio ref={audioRef} src={s.music.url} loop muted={muted} preload="auto" onLoadedMetadata={(e) => (e.currentTarget.volume = s.music.volume ?? 0.8)} data-testid="short-music-audio" />}
      {paused && <Play size={64} className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white/80" fill="currentColor" />}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/40" />
      <div className="absolute bottom-28 right-3 flex flex-col items-center gap-5 md:bottom-10">
        <Action icon={Heart} label={formatCount(s.likes)} onClick={like} active={s.liked_by_me} testid={`short-like-${s.id}`} />
        <Action icon={MessageCircle} label={formatCount(s.comments_count)} onClick={() => onComments(s)} testid={`short-comment-${s.id}`} />
        <Action icon={Share2} label={formatCount(s.shares)} onClick={share} testid={`short-share-${s.id}`} />
        {canDelete && <Action icon={Trash2} label="Excluir" onClick={() => setConfirmDel(true)} testid={`short-delete-${s.id}`} />}
      </div>
      <DeleteDialog open={confirmDel} onOpenChange={setConfirmDel} onConfirm={remove} />
      <div className="absolute bottom-24 left-4 right-20 md:bottom-8">
        <Link to={`/perfil/${s.author_id}`} className="mb-2 inline-flex items-center gap-2 rounded-2xl bg-white/10 p-1.5 pr-3 backdrop-blur-md transition-colors hover:bg-white/20" data-testid={`short-author-link-${s.id}`}>
          {s.author_role === "institution" ? <InstitutionMark name={s.author_name} logo={s.author_avatar} color={s.author_color} size={34} /> : <Avatar name={s.author_name} src={s.author_avatar} size={34} />}
          <span className="text-sm font-extrabold" data-testid="short-author">{s.author_name}</span>
          {s.author_verified && <VerifiedBadge size={16} />}
        </Link>
        {s.destination_name !== s.author_name && (s.destination_id
          ? <Link to={`/perfil/${s.destination_id}`} className="block text-xs font-semibold text-[hsl(var(--gold))] hover:underline" data-testid="short-destination">em {s.destination_name}</Link>
          : <p className="text-xs font-semibold text-[hsl(var(--gold))]" data-testid="short-destination">em {s.destination_name}</p>)}
        {s.sponsored_active && <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--gold))] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#0B0F17]" data-testid={`short-sponsored-badge-${s.id}`}>Patrocinado</span>}
        <p className="mt-1 text-sm leading-snug text-white/95 line-clamp-3" data-testid="short-caption">{s.caption}</p>
        {s.music && (
          <p className="mt-2 flex max-w-full items-center gap-1.5 text-xs font-semibold text-white/90" data-testid="short-music-chip">
            <Music2 size={13} className="shrink-0 animate-pulse text-[hsl(var(--gold))]" /> <span className="truncate">{s.music.title} · {s.music.artist}</span>
          </p>
        )}
        <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-black/40 px-2.5 py-1 text-[10px] font-mono-k text-white/70" data-testid="short-storage-info">
          <HardDrive size={11} /> {s.storage?.provider === "Cloudinary" ? "Cloudinary" : s.storage?.codec} · {s.storage?.resolution} · {s.storage?.compressed_mb}MB{s.storage?.savings_pct ? ` (-${s.storage.savings_pct}%)` : ""}
        </p>
      </div>
    </article>
  );
}
